import {
  Box,
  Flex,
  Show,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import UTMLink from "@components/UTMLink";
import { LSTIconMapping, toFixedWithoutRounding } from "@constants/common";
import { TTnxHistory } from "@constants/dapp";
import React, { memo } from "react";
import { formatEther } from "viem";

type TTnxHistoryProps = {
  tnxData: TTnxHistory[];
};

const COLUMN = ["Amount", "Status", "Time", ""];
const TnxHistory = ({ tnxData }: TTnxHistoryProps) => {
  return (
    <>
      <Show below="sm">
        <TnxTableSM tnxData={tnxData} />
      </Show>
      <Show above="sm">
        <TnxTableMD tnxData={tnxData} />
      </Show>
    </>
  );
};

export default memo(TnxHistory);

const TnxTableMD = ({ tnxData }: TTnxHistoryProps) => {
  return (
    <Table variant="unstyled">
      <Thead>
        <Tr>
          {COLUMN.map((col, idx) => {
            return (
              <Th key={`tnx_th_${idx}`} {...(idx === 0 ? { px: 1 } : {})}>
                <Text fontSize={"14px"} fontWeight="400">
                  {col}
                </Text>
              </Th>
            );
          })}
        </Tr>
      </Thead>
      <Tbody>
        {tnxData.map((item, idx) => (
          <Tr key={idx}>
            <Td px={0} width="25%">
              <Box display="flex" alignItems="center" gap={3}>
                {LSTIconMapping[item.asset] && (
                  <BaseImage
                    width={32}
                    height={32}
                    alt=""
                    src={LSTIconMapping[item.asset]}
                  />
                )}
                <Box>
                  <Text fontSize={14}>{item.asset}</Text>
                  <Text fontSize={16}>
                    {
                      +toFixedWithoutRounding(
                        formatEther(item.amountBurned as unknown as bigint),
                        6
                      )
                    }
                  </Text>
                </Box>
              </Box>
            </Td>
            <Td width="25%">
              <Box
                bgColor="#6E9BDE"
                color="#ffffff"
                p="4px 6px"
                borderRadius="3px"
                width="81px"
                height="22px"
                textAlign="center"
              >
                <Text fontSize="12px" lineHeight="14px" fontWeight={500}>
                  {item.status === "1" ? "Completed" : ""}
                </Text>
              </Box>
            </Td>
            <Td>
              <Text fontSize={"12px"} lineHeight={"24px"} fontWeight={500}>
                {item.blockTimestamp}
              </Text>
            </Td>
            <Td>
              <UTMLink
                isExternal
                href={`https://etherscan.io/tx/${item.transactionHash}`}
              >
                <Box width={5} height={5} pt={{ base: "2px", xl: 0 }}>
                  <BaseImage
                    src="/assets/external-link-blue.svg"
                    width={24}
                    height={24}
                    alt=""
                  />
                </Box>
              </UTMLink>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
};

const TnxTableSM = ({ tnxData }: TTnxHistoryProps) => {
  const getDateOrTime = (date: string) => {
    const thirdSpaceIndex = date.split(" ", 3).join(" ").length;
    const dateStr = date.substring(0, thirdSpaceIndex);
    const timeStr = date.substring(thirdSpaceIndex + 1);
    return { dateStr, timeStr };
  };
  return (
    <>
      <Flex justifyContent="space-between" mb={4}>
        {COLUMN.map((col, idx) => {
          return (
            <Text fontSize={"12px"} fontWeight="400" key={`tnx_th_${idx}`}>
              {col}
            </Text>
          );
        })}
        <Box></Box>
      </Flex>
      {tnxData.map((item, idx) => {
        return (
          <Flex
            key={idx}
            justifyContent="space-between"
            alignItems={"center"}
            py={2}
          >
            <Flex direction={"row"} gap={1}>
              <Text fontSize={12}>
                {
                  +toFixedWithoutRounding(
                    formatEther(item.amountBurned as unknown as bigint),
                    6
                  )
                }
              </Text>
              <Text fontSize={12}>{item.asset}</Text>
            </Flex>
            <Flex
              key={`status_${idx}`}
              color="#6E9BDE"
              p="4px 6px 4px 6px"
              borderRadius={"3px"}
              width={"81px"}
              height={"22px"}
              textAlign={"center"}
            >
              <Text fontSize={"10px"} lineHeight={"12px"} fontWeight={500}>
                {item.status == "1" ? "Completed" : ""}
              </Text>
            </Flex>
            <Flex w={"80px"} direction={"column"}>
              <Text fontSize={"10px"} lineHeight={"14px"} fontWeight={400}>
                {getDateOrTime(item.blockTimestamp).dateStr}
              </Text>
              <Text fontSize={"10px"} lineHeight={"14px"} fontWeight={400}>
                {getDateOrTime(item.blockTimestamp).timeStr}
              </Text>
            </Flex>
            <Flex>
              <UTMLink
                isExternal
                //TODO: only ethereum network - add dyanmic
                href={`https://etherscan.io/tx/${item.transactionHash}`}
              >
                <Flex gap={1}>
                  <Box width={5} height={5} pt={{ base: "2px", xl: 2 }}>
                    <BaseImage
                      src="/assets/external-link-blue.svg"
                      width={16}
                      height={16}
                      alt=""
                    />
                  </Box>
                </Flex>
              </UTMLink>
            </Flex>
          </Flex>
        );
      })}
    </>
  );
};
